import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SuccessCase } from '../../../../types/successCase';
import HelpModal, { HelpStep } from '../../../../components/feature/HelpModal';

const SUCCESS_TABLE_HELP: HelpStep[] = [
  {
    icon: 'ri-list-check',
    title: '成婚事例一覧の見方',
    description: '登録された成婚事例が一覧で表示されます。写真・年齢・活動期間・コメント・ステータスと表示順を一目で確認できます。',
  },
  {
    icon: 'ri-draggable',
    title: '並び替え（ドラッグ&ドロップ）',
    description: '各行の左端にある「⠿」アイコンをドラッグすると、表示順を自由に並び替えられます。離したときに自動で保存されます。',
    tips: ['数字が小さい順（1, 2, 3…）で上から表示されます', '並び替え後は「並び順を保存しました」と表示されます'],
  },
  {
    icon: 'ri-toggle-line',
    title: 'ステータスの切り替え',
    description: '「公開中」または「下書き」のバッジをクリックするだけで即座に切り替えられます。モーダルを開かずにすばやく変更できます。',
    tips: ['下書き状態の事例は成婚事例ページに表示されません'],
  },
  {
    icon: 'ri-edit-line',
    title: '事例を編集する',
    description: '「編集」ボタンをクリックすると編集モーダルが開きます。年齢・活動期間・写真・コメントを変更できます。',
  },
  {
    icon: 'ri-delete-bin-line',
    title: '事例を削除する',
    description: '「削除」ボタンをクリックすると確認メッセージが表示されます。「はい」を押すと永久削除されます。',
    tips: ['削除した事例は元に戻せません。慎重に確認してください'],
  },
];

interface Props {
  cases: SuccessCase[];
  onEdit: (item: SuccessCase) => void;
  onDelete: (id: string) => void;
  onStatusToggle: (id: string, current: 'published' | 'draft') => void;
  onReorder: (reordered: SuccessCase[]) => void;
}

interface RowProps {
  item: SuccessCase;
  onEdit: (item: SuccessCase) => void;
  onDelete: (id: string) => void;
  onStatusToggle: (id: string, current: 'published' | 'draft') => void;
  confirmId: string | null;
  setConfirmId: (id: string | null) => void;
  isDragging?: boolean;
}

function SortableRow({ item, onEdit, onDelete, onStatusToggle, confirmId, setConfirmId }: RowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isDraft = item.status === 'draft';

  return (
    <tr
      ref={setNodeRef}
      style={{
        ...style,
        borderBottom: '1px solid #f0e8e0',
        opacity: isSortableDragging ? 0.4 : isDraft ? 0.75 : 1,
        background: isSortableDragging ? '#fff8f2' : undefined,
      }}
      className="hover:bg-[#fffaf6] transition-colors"
    >
      {/* Drag Handle */}
      <td className="py-3 px-3">
        <button
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-7 h-7 rounded cursor-grab active:cursor-grabbing transition-colors hover:bg-[#f0e8e0]"
          style={{ color: '#c4b4a8', touchAction: 'none' }}
          title="ドラッグして並び替え"
        >
          <i className="ri-draggable text-base"></i>
        </button>
      </td>

      {/* 写真 */}
      <td className="py-3 px-4">
        {item.image_url ? (
          <div className="w-16 h-12 rounded overflow-hidden shrink-0">
            <img src={item.image_url} alt={item.ages} className="w-full h-full object-cover object-center" loading="lazy" />
          </div>
        ) : (
          <div className="w-16 h-12 rounded flex items-center justify-center" style={{ background: '#f0e8e0' }}>
            <i className="ri-image-line" style={{ color: '#c4b4a8' }}></i>
          </div>
        )}
      </td>

      {/* 年齢 */}
      <td className="py-3 px-4">
        <span className="font-bold text-sm" style={{ color: '#8b4513' }}>{item.ages}</span>
      </td>

      {/* 活動期間 */}
      <td className="py-3 px-4">
        <span
          className="inline-block px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap"
          style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}
        >
          {item.active_period}
        </span>
      </td>

      {/* コメント */}
      <td className="py-3 px-4">
        <p className="line-clamp-2 text-sm" style={{ color: '#6b5a4e' }}>
          {item.excerpt}
        </p>
      </td>

      {/* ステータス */}
      <td className="py-3 px-4">
        <button
          onClick={() => onStatusToggle(item.id, item.status)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-opacity hover:opacity-75 cursor-pointer whitespace-nowrap"
          style={isDraft
            ? { background: '#fdf5e8', color: '#c4903a', border: '1px solid #f0d8a0' }
            : { background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }
          }
          title={isDraft ? 'クリックで公開する' : 'クリックで下書きに戻す'}
        >
          <span className="flex items-center justify-center w-3 h-3">
            <i className={`text-xs ${isDraft ? 'ri-draft-line' : 'ri-global-line'}`}></i>
          </span>
          {isDraft ? '下書き' : '公開中'}
        </button>
      </td>

      {/* 順番 */}
      <td className="py-3 px-4">
        <span className="text-sm font-bold" style={{ color: '#9b8b7f' }}>{item.sort_order}</span>
      </td>

      {/* 操作 */}
      <td className="py-3 px-4">
        {confirmId === item.id ? (
          <div className="flex items-center gap-2">
            <span style={{ color: '#cc4444', fontSize: '12px' }} className="whitespace-nowrap">削除しますか？</span>
            <button
              onClick={() => { onDelete(item.id); setConfirmId(null); }}
              className="px-2 py-1 rounded text-xs font-bold text-white whitespace-nowrap cursor-pointer"
              style={{ background: '#cc4444' }}
            >
              はい
            </button>
            <button
              onClick={() => setConfirmId(null)}
              className="px-2 py-1 rounded text-xs font-bold whitespace-nowrap cursor-pointer"
              style={{ background: '#f0e8e0', color: '#6b5a4e' }}
            >
              いいえ
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(item)}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
              style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}
            >
              <span className="flex items-center justify-center w-3 h-3"><i className="ri-edit-line text-xs"></i></span>
              編集
            </button>
            <button
              onClick={() => setConfirmId(item.id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
              style={{ background: '#fff0f0', color: '#cc4444', border: '1px solid #f8c4c4' }}
            >
              <span className="flex items-center justify-center w-3 h-3"><i className="ri-delete-bin-line text-xs"></i></span>
              削除
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function SuccessCaseTable({ cases, onEdit, onDelete, onStatusToggle, onReorder }: Props) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = cases.findIndex((c) => c.id === active.id);
    const newIndex = cases.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(cases, oldIndex, newIndex).map((item, idx) => ({
      ...item,
      sort_order: idx + 1,
    }));
    onReorder(reordered);
  };

  if (cases.length === 0) {
    return (
      <div className="text-center py-20" style={{ color: '#9b8b7f' }}>
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ background: '#f2f8f4' }}>
          <i className="ri-heart-line text-3xl" style={{ color: '#5a9e6f' }}></i>
        </div>
        <p className="text-lg">成婚事例がありません。新規追加してください。</p>
      </div>
    );
  }

  const activeItem = activeId ? cases.find((c) => c.id === activeId) : null;

  return (
    <>
      <HelpModal
        title="成婚事例一覧の使い方"
        sections={[{ label: '使い方', steps: SUCCESS_TABLE_HELP }]}
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e8d8cc', background: '#fff8f2' }}>
                <th className="py-3 px-3 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '44px' }}></th>
                <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '80px' }}>写真</th>
                <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '120px' }}>年齢</th>
                <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '140px' }}>活動期間</th>
                <th className="py-3 px-4 text-sm font-bold" style={{ color: '#6b5a4e' }}>コメント</th>
                <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '100px' }}>ステータス</th>
                <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '80px' }}>順番</th>
                <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '180px' }}>
                  <div className="flex items-center gap-2">
                    操作
                    <button
                      onClick={() => setHelpOpen(true)}
                      className="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-opacity hover:opacity-70"
                      style={{ background: '#fff0d8', color: '#c4903a', border: '1px solid #f0d8a0' }}
                      title="使い方を見る"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <SortableContext items={cases.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {cases.map((item) => (
                  <SortableRow
                    key={item.id}
                    item={item}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onStatusToggle={onStatusToggle}
                    confirmId={confirmId}
                    setConfirmId={setConfirmId}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </div>

        <DragOverlay>
          {activeItem && (
            <table className="w-full text-left" style={{ borderCollapse: 'collapse', opacity: 0.95 }}>
              <tbody>
                <tr
                  style={{
                    borderBottom: '1px solid #f0e8e0',
                    background: '#fff8f2',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    display: 'table-row',
                  }}
                >
                  <td className="py-3 px-3">
                    <div className="flex items-center justify-center w-7 h-7">
                      <i className="ri-draggable text-base" style={{ color: '#c4b4a8' }}></i>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {activeItem.image_url ? (
                      <div className="w-16 h-12 rounded overflow-hidden">
                        <img src={activeItem.image_url} alt={activeItem.ages} className="w-full h-full object-cover object-center" />
                      </div>
                    ) : (
                      <div className="w-16 h-12 rounded flex items-center justify-center" style={{ background: '#f0e8e0' }}>
                        <i className="ri-image-line" style={{ color: '#c4b4a8' }}></i>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-bold text-sm" style={{ color: '#8b4513' }}>{activeItem.ages}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}>
                      {activeItem.active_period}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <p className="line-clamp-2 text-sm" style={{ color: '#6b5a4e' }}>{activeItem.excerpt}</p>
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tbody>
            </table>
          )}
        </DragOverlay>
      </DndContext>
    </>
  );
}